import { ActionIcon, Group, Paper, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import dayjs from 'dayjs';
import React from 'react';
import {
  HiOutlineDocument,
  HiOutlineDocumentText,
  HiOutlineExternalLink,
  HiOutlinePhotograph,
  HiOutlineVideoCamera
} from 'react-icons/hi';
import { MaterialData } from 'types/lcms/courses';
import styles from './styles.module.css';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconFileTypeXls, IconX } from '@tabler/icons-react';
import MaterialInputStatus from './MaterialInputStatus';

type TypeConfirm = 'approved' | 'rejected';
interface MaterialItemProps extends MaterialData {
  role?: 'admin' | 'sme';
  onConfirm?: (_id: string, _type: TypeConfirm) => void;
}

const MaterialItem: React.FC<MaterialItemProps> = ({
  id,
  title,
  submissionDate,
  type,
  description,
  url,
  role = 'sme',
  onConfirm,
  status
}) => {
  const [showExternalIcon, { open: openExternalIcon, close: closeExternalIcon }] =
    useDisclosure(false);
  const handleClickMaterial = () => window.open(url, '_blank');

  const materialIcon = () => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return <HiOutlineDocumentText />;
      case 'video':
        return <HiOutlineVideoCamera />;
      case 'image':
        return <HiOutlinePhotograph />;
      case 'excel':
        return <IconFileTypeXls size={16} />;
      default:
        return <HiOutlineDocument />;
    }
  };

  const handleConfirm = (_type: TypeConfirm) => {
    if (onConfirm) {
      onConfirm(id, _type);
    }
  };

  const icon = materialIcon();

  return (
    <Stack gap={8}>
      <Group justify='space-between'>
        <Group gap={4}>
          <Text fz={'sm'} fw={600} c={'dark'}>
            {title}
          </Text>
          {role === 'admin' && <MaterialInputStatus item={{ status }} />}
        </Group>
        <Text fz={'xs'}>{dayjs(submissionDate).format('HH:mm:ss, DD MMMM YYYY')}</Text>
      </Group>
      <Paper p={'md'} withBorder>
        <Group justify='space-between'>
          <UnstyledButton
            className={styles.materialItem}
            onClick={handleClickMaterial}
            onMouseEnter={() => openExternalIcon()}
            onMouseLeave={() => closeExternalIcon()}
          >
            <Group gap={4}>
              {icon}
              <Text fz={'sm'} fw={500} c={'gray'} className={styles.materialItem}>
                {description}
              </Text>
              {showExternalIcon && <HiOutlineExternalLink />}
            </Group>
          </UnstyledButton>
          {role === 'sme' && <MaterialInputStatus item={{ status }} />}
          {role === 'admin' && status?.toLowerCase() === 'pending' && (
            <Group>
              <Tooltip label={'Approve Material'}>
                <ActionIcon variant='default' c={'green'} onClick={() => handleConfirm('approved')}>
                  <IconCheck />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={'Reject Material'}>
                <ActionIcon
                  variant='default'
                  c={'destructive'}
                  onClick={() => handleConfirm('rejected')}
                >
                  <IconX />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Group>
      </Paper>
    </Stack>
  );
};

export default MaterialItem;
